import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { SearchIcon, DeleteIcon, ArrowLeftIcon, ArrowRightIcon, PencilIcon, CreateInvoiceIcon } from "../assets/dashboard-icon.svg";
import { useRef, useState } from "react";
import { useCustomers } from "../hooks/useCustomers";
import { Customer } from "../api/customer";
import { useAppDispatch } from "../hooks/useRedux";
import { deleteCustomer } from "../features/customer/customer.slice";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import AddCustomerModal from "../components/add-customer-modal/components/AddCustomerModal";
import EditCustomerModal from "../components/add-customer-modal/components/EditCustomerModal";
import { DashboardButton } from "./dashboard/dashboard-button.component";
import LoadingCard from '../components/SkeletonLoading'

export const MyClientPageSection = ({
    className,
}: {
    className?: string
}
) => {
    const { t } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isMobile = window.innerWidth <= 768;
    const dispatch = useAppDispatch();
    const [deletingCustomerId, setDeletingCustomerId] = useState<number | null>(null);
    const lang = new URLSearchParams(window.location.search).get('lang') || 'en-us';
    const customerSearchParam = new URLSearchParams(window.location.search).get('customerSearch');
    const searchParam = new URLSearchParams(window.location.search).get('search');
    const customerSearchVal = customerSearchParam ? decodeURIComponent(customerSearchParam) : '';
    const searchVal = searchParam ? decodeURIComponent(searchParam) : '';
    const combinedSearchVal = [customerSearchVal, searchVal].filter(Boolean).join(' + ');
    const navigate = useNavigate();
    const {
        loading,
        error,
        searchQuery,
        currentPage,
        totalPages,
        paginatedCustomers,
        setSearchQuery,
        setCurrentPage,
        goToNextPage,
        goToPreviousPage,
        refetch,
    } = useCustomers(10, combinedSearchVal);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleDeleteCustomer = async (customerId: number) => {
        try {
            setDeletingCustomerId(customerId);
            await dispatch(deleteCustomer(customerId)).unwrap();
            toast.success(String(t('DELETE_SUCCESS')));
            await refetch();
        } catch (error: any) {
            const msg = typeof error === 'string' ? error : (error?.message || t('DELETE_FAILED'));
            toast.error(String(msg));
        } finally {
            setDeletingCustomerId(null);
        }
    };

    const handleEditCustomer = (customer: Customer) => {
        setSelectedCustomer(customer);
        setShowEditModal(true);
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    if (loading) {
        return (
            <div className={`invoice-table-container ${className}`}>
                <div className="invoice-table-header">
                    <h3 className="invoice-table-title">{t('MY_INVOICES')}</h3>
                </div>
                <LoadingCard/>
            </div>
        );
    }


    return (
        <div className={`invoice-table-container ${className}`}>
            <div className="invoice-table-header">
                <h3 className="invoice-table-title">{t('MY_CLIENTS')}</h3>

                <div className="invoice-table__filter-bar">
                    <div className="invoice-table__search-input" ref={dropdownRef}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('HEADER_SEARCH') || 'Search'}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="invoice-table__search-field"
                        />
                    </div>
                    <DashboardButton
                        variant="primary"
                        // icon={<PlusIcon />}
                        onClick={() => setShowCustomerModal(true)}
                    >
                        {t('ADD_CLIENT')}
                    </DashboardButton>
                </div>
            </div>

            <div className="invoice-table-content">
                {/* Table */}
                {!loading && !error && paginatedCustomers.length >= 0 && (
                    <div className="invoice-table">
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>{t('ID') || 'Id'}</TableColumn>
                                <TableColumn>{t('CLIENT_NAME') || 'Client Name'}</TableColumn>
                                <TableColumn>{t('ORG_NO') || 'Org No'}</TableColumn>
                                <TableColumn>{t('REFERENCE') || 'Reference'}</TableColumn>
                                <TableColumn>{t('ADDRESS') || 'Address'}</TableColumn>
                                <TableColumn>{t('ACTION_LABEL') || 'Action'}</TableColumn>
                            </TableHeader>
                        )}

                        <TableBody>
                            {paginatedCustomers.length === 0 && (
                                <div className="invoice-table">
                                    <div className="invoice-table-content">
                                        {/* No drafts found message */}
                                        {(paginatedCustomers.length === 0) && (
                                            <div className='d-flex justify-content-center flex-column align-items-center pt-5'>
                                                <span className='pt-3'>{t('NO_CUSTOMERS')}.</span>
                                                {error}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {loading && (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                    {t('LOADING_CUSTOMERS') || 'Loading customers...'}
                                </div>
                            )}
                            {isMobile ? (
                                paginatedCustomers?.map((customer: Customer) => (
                                    <MobileCardWrapper key={customer.customerId}>
                                        <MobileCardRow label={t('ID') || 'Customer Name'}>
                                            <span className="invoice-table-detailed__invoice-link">{customer.customerId}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CLIENT_NAME') || 'Email'}>
                                            {customer.name || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('ORG_NO') || 'Phone'}>
                                            {customer.orgNo || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('REFERENCE') || 'Address'}>
                                            {customer.reference || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('ADDRESS') || 'City'}>
                                            {customer.address || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('ACTION_LABEL') || 'Action'}>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("EDIT_CUSTOMER_MODAL_TITLE")}
                                                    onClick={() => handleEditCustomer(customer)}
                                                >
                                                    <PencilIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("CREATE_INVOICE")}
                                                    onClick={() => navigate(`/v2/create-invoice?lang=${lang}&customerId=${customer.customerId}`)} // Added onClick handler
                                                >
                                                    <CreateInvoiceIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("DELETE")}
                                                    onClick={() => handleDeleteCustomer(customer.customerId!)}
                                                    disabled={deletingCustomerId === customer.customerId}
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            </div>
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                paginatedCustomers.map((customer: Customer) => (
                                    <TableRow key={customer.customerId}>
                                        <TableCell>
                                            <span className="invoice-table-detailed__invoice-link">{customer.customerId}</span>
                                        </TableCell>
                                        <TableCell>{customer.name || 'N/A'}</TableCell>
                                        <TableCell>{customer.orgNo || 'N/A'}</TableCell>
                                        <TableCell>{customer.reference || 'N/A'}</TableCell>
                                        <TableCell>{customer.address || 'N/A'}</TableCell>
                                        <TableCell>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("EDIT_CUSTOMER_MODAL_TITLE")}
                                                    onClick={() => handleEditCustomer(customer)}
                                                >
                                                    <PencilIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("CREATE_INVOICE")}
                                                    onClick={() => navigate(`/v2/create-invoice?lang=${lang}&customerId=${customer.customerId}`)} // Added onClick handler
                                                >
                                                    <CreateInvoiceIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("DELETE")}
                                                    onClick={() => handleDeleteCustomer(customer.customerId!)}
                                                    disabled={deletingCustomerId === customer.customerId}
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>
                )}


                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP') || 'Prev'}</span>
                            </button>

                            {getPageNumbers().map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => setCurrentPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>
                                <ArrowRightIcon />
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <AddCustomerModal
                show={showCustomerModal}
                onHide={() => setShowCustomerModal(false)}
                onSave={() => {
                    setShowCustomerModal(false);
                    refetch();
                }}
            />
            <EditCustomerModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setSelectedCustomer(null);
                }}
                onSave={() => {
                    setShowEditModal(false);
                    setSelectedCustomer(null);
                    refetch();
                }}
                selectedCustomer={selectedCustomer?.customerId}
            />
        </div>
    );
}